import { ZodiosError } from "@zodios/core";
import type { AxiosResponse } from "axios";

/**
 * Diagnostics for TRELLIS-UI-4A5: `GET /api/userprofile/` intermittently returns a 200 whose body is
 * missing `initial_data_loaded_on`, which the response schema requires, so session bootstrap fails.
 *
 * The backend serializer always emits that key (it is declared with `default=None`, verified against
 * the pinned DRF version), and no code path drops it, so we cannot tell from the payload alone where
 * the field is lost. These diagnostics record what actually arrived on the wire so the next
 * occurrence identifies the hop responsible.
 *
 * Only response header values and top-level key *names* are captured -- never body values -- so no
 * customer data is attached to the event. Cross-origin responses expose only the CORS-safelisted
 * headers (content-type, content-length, cache-control, expires, last-modified, pragma) unless the
 * API adds `Access-Control-Expose-Headers`.
 */
type ResponseDiagnostics = {
  method: string | undefined;
  requestUrl: string | undefined;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  bodyKeys: string[] | null;
  bodyKeyCount: number | null;
};

type ZodiosValidationFailure = {
  endpoint: string;
  failedPaths: string[] | undefined;
  receivedKeys: string[] | null;
  receivedKeyCount: number | null;
  /** Every recorded response for this endpoint, so concurrent duplicate requests can be compared. */
  responses: ResponseDiagnostics[];
};

const MAX_TRACKED_RESPONSES = 16;
const MAX_RECORDED_KEYS = 100;
const SENSITIVE_HEADERS = new Set(["authorization", "cookie", "set-cookie", "proxy-authorization"]);

let recentResponses: ResponseDiagnostics[] = [];

function readHeaders(response: AxiosResponse): Record<string, string> {
  const raw: unknown = response.headers;
  if (typeof raw !== "object" || raw === null) {
    return {};
  }
  const toJSON = (raw as { toJSON?: unknown }).toJSON;
  const source: unknown = typeof toJSON === "function" ? toJSON.call(raw) : raw;
  if (typeof source !== "object" || source === null) {
    return {};
  }
  const headers: Record<string, string> = {};
  for (const [name, value] of Object.entries(source)) {
    const key = name.toLowerCase();
    if (value === undefined || value === null || SENSITIVE_HEADERS.has(key)) {
      continue;
    }
    headers[key] = String(value);
  }
  return headers;
}

function readBodyKeys(body: unknown): { bodyKeys: string[] | null; bodyKeyCount: number | null } {
  if (typeof body !== "object" || body === null || Array.isArray(body)) {
    return { bodyKeys: null, bodyKeyCount: null };
  }
  const keys = Object.keys(body);
  return { bodyKeys: keys.slice(0, MAX_RECORDED_KEYS), bodyKeyCount: keys.length };
}

function normalizePath(url: string): string {
  const withoutQuery = url.split("?")[0] ?? url;
  return withoutQuery.replace(/\/+$/, "");
}

function pathsMatch(requestUrl: string, endpointPath: string): boolean {
  const request = normalizePath(requestUrl);
  const endpoint = normalizePath(endpointPath);
  return request === endpoint || request.endsWith(endpoint);
}

/**
 * Records wire-level metadata for a response. Called for every response from the shared axios
 * instance, which Zodios also uses, so the entry is always in place before validation runs.
 */
export function recordResponseDiagnostics(response: AxiosResponse): void {
  try {
    const { bodyKeys, bodyKeyCount } = readBodyKeys(response.data);
    recentResponses.push({
      method: response.config.method,
      requestUrl: response.config.url,
      status: response.status,
      statusText: response.statusText,
      headers: readHeaders(response),
      bodyKeys,
      bodyKeyCount,
    });
    if (recentResponses.length > MAX_TRACKED_RESPONSES) {
      recentResponses = recentResponses.slice(-MAX_TRACKED_RESPONSES);
    }
  } catch {
    // Diagnostics must never interfere with the request they are describing.
  }
}

function takeResponsesForEndpoint(endpointPath: string): ResponseDiagnostics[] {
  const matched: ResponseDiagnostics[] = [];
  const remaining: ResponseDiagnostics[] = [];
  for (const recorded of recentResponses) {
    if (recorded.requestUrl !== undefined && pathsMatch(recorded.requestUrl, endpointPath)) {
      matched.push(recorded);
    } else {
      remaining.push(recorded);
    }
  }
  recentResponses = remaining;
  return matched;
}

function extractFailedPaths(cause: unknown): string[] | undefined {
  if (typeof cause !== "object" || cause === null) {
    return undefined;
  }
  const issues: unknown = (cause as { issues?: unknown }).issues;
  if (!Array.isArray(issues)) {
    return undefined;
  }
  return issues.map((issue: unknown) => {
    const path: unknown = (issue as { path?: unknown }).path;
    return Array.isArray(path) ? path.join(".") : "<unknown>";
  });
}

/**
 * Builds a Sentry context for a Zodios response-validation failure, pairing the schema paths that
 * failed with the wire metadata of the response that caused it. Returns null for anything else.
 */
export function describeZodiosValidationFailure(error: unknown): ZodiosValidationFailure | null {
  if (!(error instanceof ZodiosError)) {
    return null;
  }
  const method = error.config?.method;
  const url = error.config?.url;
  const { bodyKeys, bodyKeyCount } = readBodyKeys(error.data);
  return {
    endpoint: `${method ?? "?"} ${url ?? "?"}`,
    failedPaths: extractFailedPaths(error.cause),
    receivedKeys: bodyKeys,
    receivedKeyCount: bodyKeyCount,
    responses: url === undefined ? [] : takeResponsesForEndpoint(url),
  };
}
